import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../hooks/useTier';
import { useToast } from '../context/ToastContext';
import { db, storage } from '../config/firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import Skeleton from './Skeleton';
import { checkLimit } from '../utils/tierUtils';

export default function RecordingsManager() {
  const { user, userProfile } = useAuth();
  const { isPremium } = useTier();
  const toast = useToast();
  const [recordings, setRecordings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [previewRecording, setPreviewRecording] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);

  const recordingLimit = checkLimit(userProfile, 'recordings', recordings.length);
  const maxRecordings = recordingLimit.limit;
  const maxRecordingsDisplay = recordingLimit.limit === -1 ? 'Unlimited' : recordingLimit.limit;

  useEffect(() => {
    if (user) {
      loadRecordings();
    }
  }, [user]);

  const loadRecordings = async () => {
    setIsLoading(true);
    try {
      const recordingsRef = collection(db, 'users', user.uid, 'recordings');
      const snapshot = await getDocs(recordingsRef);

      const recordingsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by creation date (newest first)
      recordingsList.sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || 0;
        const dateB = b.createdAt?.toMillis?.() || 0;
        return dateB - dateA;
      });

      setRecordings(recordingsList);
    } catch (error) {
      toast.error('Failed to load recordings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (downloadUrl, fileName) => {
    try {
      toast.info('Preparing download...');

      // Fetch the file as a blob to bypass Firebase Storage's Content-Disposition
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Create temporary anchor element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'recording.webm';

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

      toast.success('Download started!');
    } catch (error) {
      toast.error('Failed to download recording. Try right-click → Save As');
    }
  };

  const handleDelete = async (recording) => {
    if (deletingId) return; // Prevent multiple deletions at once

    setDeletingId(recording.id);
    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, recording.storagePath);
      await deleteObject(storageRef);

      // Delete from Firestore
      await deleteDoc(doc(db, 'users', user.uid, 'recordings', recording.id));

      // Decrement recording count
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const currentCount = userDoc.data()?.usage?.recordingsCount || 0;
      await updateDoc(userRef, {
        'usage.recordingsCount': Math.max(0, currentCount - 1)
      });

      // Update local state
      setRecordings(prev => prev.filter(r => r.id !== recording.id));

      toast.success('Recording deleted');
      setConfirmDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete recording');
    } finally {
      setDeletingId(null);
    }
  };

  const handleArchive = async (recording) => {
    if (archivingId) return;

    setArchivingId(recording.id);
    try {
      const recordingRef = doc(db, 'users', user.uid, 'recordings', recording.id);
      await updateDoc(recordingRef, {
        archived: true,
        archivedAt: serverTimestamp()
      });

      // Update local state
      setRecordings(prev => prev.map(r =>
        r.id === recording.id ? { ...r, archived: true, archivedAt: new Date() } : r
      ));

      toast.success('Recording archived');
    } catch (error) {
      toast.error('Failed to archive recording');
    } finally {
      setArchivingId(null);
    }
  };

  const handleUnarchive = async (recording) => {
    if (archivingId) return;

    setArchivingId(recording.id);
    try {
      const recordingRef = doc(db, 'users', user.uid, 'recordings', recording.id);
      await updateDoc(recordingRef, {
        archived: false,
        archivedAt: null
      });

      // Update local state
      setRecordings(prev => prev.map(r =>
        r.id === recording.id ? { ...r, archived: false, archivedAt: null } : r
      ));

      toast.success('Recording restored');
    } catch (error) {
      toast.error('Failed to restore recording');
    } finally {
      setArchivingId(null);
    }
  };

  // Filter recordings based on archived state
  const filteredRecordings = recordings.filter(r =>
    showArchived ? r.archived : !r.archived
  );

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) return `${remainingSeconds}s`;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 MB';
    const megabytes = bytes / (1024 * 1024);
    return `${megabytes.toFixed(2)} MB`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return 'Unknown date';
    return timestamp.toDate().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isAtLimit = maxRecordings !== -1 && recordings.length >= maxRecordings;

  return (
    <div className="min-h-screen bg-white dark:bg-[#000000] transition-colors duration-300 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#1d1d1f] dark:text-white mb-2">
            Recordings
          </h1>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-[#6e6e73] dark:text-[#8e8e93]">
              {recordings.filter(r => !r.archived).length} / {maxRecordingsDisplay} recording{(typeof maxRecordingsDisplay === 'number' && maxRecordingsDisplay > 1) || maxRecordingsDisplay === 'Unlimited' ? 's' : ''} used
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  showArchived
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                    : 'bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e]'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                {showArchived ? 'Show Active' : 'Show Archived'}
              </button>
              {!isPremium && isAtLimit && (
                <div className="bg-gradient-to-r from-[#06c] to-[#0055d4] text-white px-4 py-2 rounded-full text-sm font-medium">
                  Upgrade for more storage
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quota Warning */}
        {!isPremium && isAtLimit && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                  Storage Limit Reached
                </h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  You've reached your free tier limit. Delete a recording to make room for new ones, or upgrade to Premium for 10 recordings.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-3xl p-6 border border-transparent dark:border-[#3c3c3e] animate-pulse">
                <Skeleton variant="rectangular" className="w-full h-40 mb-4" />
                <Skeleton variant="text" className="w-3/4 mb-2" />
                <Skeleton variant="text" className="w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredRecordings.length === 0 && (
          <div className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-3xl p-12 text-center border border-transparent dark:border-[#3c3c3e]">
            <div className="w-20 h-20 bg-[#d2d2d7] dark:bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-[#86868b] dark:text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-2">
              {showArchived ? 'No archived recordings' : 'No recordings'}
            </h3>
            <p className="text-[#6e6e73] dark:text-[#8e8e93]">
              {showArchived ? 'Archived recordings will appear here' : 'Start recording your meetings to save them here'}
            </p>
          </div>
        )}

        {/* Recordings Grid */}
        {!isLoading && filteredRecordings.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredRecordings.map((recording) => (
              <div
                key={recording.id}
                className="bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed] dark:from-[#1c1c1e] dark:to-[#2c2c2e] rounded-3xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              >
                {/* Video Preview Placeholder */}
                <div className="w-full aspect-video bg-[#d2d2d7] dark:bg-[#3a3a3c] rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
                  <svg className="w-16 h-16 text-[#86868b] dark:text-[#6e6e73]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>

                {/* Recording Info */}
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white text-sm truncate flex-1 mr-2">
                      {recording.meetingName || recording.fileName || 'Recording'}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                      recording.quality === '1080p'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    }`}>
                      {recording.quality || '720p'}
                    </span>
                  </div>

                  {/* Metadata Grid */}
                  <div className="space-y-1.5 text-xs text-[#6e6e73] dark:text-[#8e8e93]">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="font-mono truncate">{recording.meetingId || 'Unknown'}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{formatDuration(recording.duration)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                      </svg>
                      <span>{formatFileSize(recording.fileSize)}</span>
                    </div>

                    {recording.participants && recording.participants.length > 0 && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <span>{recording.participants.length} participant{recording.participants.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{formatDate(recording.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {confirmDeleteId === recording.id ? (
                  <div className="space-y-2">
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium text-center mb-2">
                      Delete this recording?
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        disabled={deletingId === recording.id}
                        className="flex-1 px-4 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-full text-sm font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDelete(recording)}
                        disabled={deletingId === recording.id}
                        className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {deletingId === recording.id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Deleting...
                          </>
                        ) : (
                          'Delete'
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPreviewRecording(recording)}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-full text-sm font-medium hover:shadow-lg hover:scale-105 transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Preview
                      </button>
                      <button
                        onClick={() => handleDownload(recording.downloadUrl, recording.fileName)}
                        className="flex-1 px-4 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-full text-sm font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </button>
                    </div>
                    {/* Archive/Unarchive button */}
                    {recording.archived ? (
                      <button
                        onClick={() => handleUnarchive(recording)}
                        disabled={archivingId === recording.id}
                        className="w-full px-4 py-2.5 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-full text-sm font-medium hover:bg-green-100 dark:hover:bg-green-900/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {archivingId === recording.id ? 'Restoring...' : 'Restore'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleArchive(recording)}
                        disabled={archivingId === recording.id}
                        className="w-full px-4 py-2.5 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-full text-sm font-medium hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        {archivingId === recording.id ? 'Archiving...' : 'Archive'}
                      </button>
                    )}

                    {/* Delete button */}
                    <button
                      onClick={() => setConfirmDeleteId(recording.id)}
                      className="w-full px-4 py-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upgrade CTA for Free Users */}
        {!isPremium && recordings.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-3xl p-8 text-white text-center">
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl font-semibold mb-3">
                Need more storage?
              </h3>
              <p className="text-white/90 mb-6">
                Upgrade to Premium for 10 recordings, 1080p quality, and unlimited meeting time
              </p>
              <button
                onClick={() => window.location.href = '/dashboard?upgrade=true'}
                className="bg-white text-[#06c] px-8 py-3 rounded-full font-semibold hover:shadow-2xl hover:scale-105 transition-all"
              >
                Upgrade to Premium
              </button>
            </div>
          </div>
        )}

        {/* Video Preview Modal */}
        {previewRecording && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6 animate-fade-in">
            <div className="bg-[#1c1c1e] rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-[#3c3c3e]">
              {/* Modal Header */}
              <div className="bg-[#2c2c2e] border-b border-[#3c3c3e] px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
                <div className="flex-1 min-w-0 mr-2">
                  <h2 className="text-lg sm:text-xl font-semibold text-white truncate">
                    {previewRecording.meetingName || previewRecording.fileName}
                  </h2>
                  <p className="text-xs sm:text-sm text-[#8e8e93] mt-1 truncate">
                    {formatDuration(previewRecording.duration)} • {formatFileSize(previewRecording.fileSize)} • {previewRecording.quality}
                  </p>
                </div>
                <button
                  onClick={() => setPreviewRecording(null)}
                  className="p-2 hover:bg-[#3c3c3e] rounded-lg transition-colors"
                  title="Close"
                >
                  <svg className="w-6 h-6 text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Video Player */}
              <div className="p-4 sm:p-6">
                <video
                  src={previewRecording.downloadUrl}
                  controls
                  autoPlay
                  className="w-full rounded-xl sm:rounded-2xl bg-black"
                  style={{ maxHeight: '60vh' }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>

              {/* Modal Footer */}
              <div className="bg-[#2c2c2e] border-t border-[#3c3c3e] px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-sm text-[#8e8e93]">
                  {previewRecording.participants && previewRecording.participants.length > 0 && (
                    <span>{previewRecording.participants.length} participant{previewRecording.participants.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setPreviewRecording(null)}
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-[#1c1c1e] text-white border border-[#3c3c3e] rounded-xl font-medium hover:bg-[#3c3c3e] transition-all text-sm"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      handleDownload(previewRecording.downloadUrl, previewRecording.fileName);
                      setPreviewRecording(null);
                    }}
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
